import React from "react";
import axios from "axios";
import { toast } from "sonner";
import { useI18n } from "../i18n/I18nProvider";
import { Phone, Check } from "lucide-react";
import { API } from "../lib/api";

const TIMES = ["asap", "morning", "afternoon", "evening"];

export default function CallbackPage() {
  const { t, locale } = useI18n();
  const [form, setForm] = React.useState({ full_name: "", phone: "", best_time: "asap", topic: "" });
  const [submitting, setSubmitting] = React.useState(false);
  const [done, setDone] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/callback-request`, { ...form, locale });
      setDone(true);
      toast.success(t("callback.success"));
    } catch (err) {
      toast.error(t("common.error_submit"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="callback-page">
      <section className="relative pt-16 pb-12 sm:pt-24 sm:pb-16 border-b border-zinc-200 overflow-hidden">
        <div className="absolute inset-0 technical-grid pointer-events-none" />
        <div className="container-oj relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-6">
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-zinc-500">{t("callback.eyebrow")}</div>
            <h1 className="font-display mt-4 text-balance text-5xl sm:text-7xl font-black tracking-tighter leading-[0.95] text-oj-ink max-w-3xl">
              {t("callback.title")}
            </h1>
            <p className="mt-5 max-w-xl text-lg text-zinc-600">{t("callback.sub")}</p>
          </div>

          <div className="lg:col-span-6 lg:col-start-7">
            <div className="rounded-2xl border border-zinc-200 p-7 bg-white shadow-sm">
              {done ? (
                <div className="text-center py-10" data-testid="callback-page-success">
                  <div className="mx-auto size-12 rounded-full bg-oj-kiosk text-white grid place-items-center"><Check className="size-6" /></div>
                  <div className="mt-4 font-display text-2xl font-bold text-oj-ink">{t("callback.success")}</div>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-3" data-testid="callback-page-form">
                  <input
                    required
                    placeholder={t("callback.full_name")}
                    value={form.full_name}
                    onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                    data-testid="callback-page-name"
                    className="w-full bg-zinc-50 border border-zinc-200 rounded-md px-4 py-3 text-sm focus:outline-none focus:border-oj-ink"
                  />
                  <input
                    required
                    type="tel"
                    placeholder={t("callback.phone")}
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    data-testid="callback-page-phone"
                    className="w-full bg-zinc-50 border border-zinc-200 rounded-md px-4 py-3 text-sm focus:outline-none focus:border-oj-ink"
                  />
                  <input
                    placeholder={t("callback.topic")}
                    value={form.topic}
                    onChange={(e) => setForm({ ...form, topic: e.target.value })}
                    data-testid="callback-page-topic"
                    className="w-full bg-zinc-50 border border-zinc-200 rounded-md px-4 py-3 text-sm focus:outline-none focus:border-oj-ink"
                  />
                  <div>
                    <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-500 mb-2">{t("callback.best_time")}</div>
                    <div className="flex flex-wrap gap-2">
                      {TIMES.map((tm) => {
                        const active = form.best_time === tm;
                        return (
                          <button
                            type="button"
                            key={tm}
                            onClick={() => setForm({ ...form, best_time: tm })}
                            aria-pressed={active}
                            data-testid={`callback-page-time-${tm}`}
                            className={`text-xs font-semibold px-3 py-2 rounded-full border ${active ? "bg-oj-ink text-white border-transparent" : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-900"}`}
                          >
                            {t(`callback.${tm}`)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="callback-page-submit"
                    className="w-full inline-flex items-center justify-center gap-1.5 bg-oj-ink text-white font-semibold px-4 py-3 rounded-md hover:bg-zinc-800 disabled:opacity-60"
                  >
                    <Phone className="size-4" /> {submitting ? "..." : t("callback.submit")}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
